import { Box, Typography, type SelectChangeEvent } from '@mui/material';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from '@mui/material';
import {
  automationSchema,
  automationTypeOptionsMap,
  getInitialRequirementAutomation,
  getInitialProgressAutomation,
  type ILegalRequirementAutomation,
  isRequirementAutomation,
  isProgressAutomation,
  type ILegalTransactionProgressAutomation,
  getInitialStateAutomation,
  type ILegalTransactionStateAutomation,
  isStateAutomation,
  IAutomationExecutionType,
  automationExecutionTypeOptions,
} from '../../../Interfaces/ILegalTransactionMeasure';
import { useState, type SetStateAction, type Dispatch } from 'react';
import { useAlert } from '../../../context/AlertContext';
import { IAutomation } from '../../../Interfaces/ILegalTransactionMeasure';
import { ErrorScreen } from '../../../core/ErrorScreen';
import { LoadingSpinner } from '../../../core/LoadingSpinner';
import { type ILegalRequirement } from '../../../Interfaces/ILegalRequirement';
import type { ILegalRequirementTemplate } from '../../../Interfaces/ILegalRequirementTemplate';
import type { ILegalRequirementState } from '../../../Interfaces/ILegalRequirementState';
import type { ILegalTransactionProgress } from '../../../Interfaces/ILegalTransactionProgress';
import type { ILegalTransactionState } from '../../../Interfaces/ILegalTransactionState';
import {
  useGetLegalTransactionProgresses,
  useGetLegalTransactionStates,
} from '../../../hooks/reactQuerys/LegalTransactionQueries';
import { useGetLegalRequirementStates } from '../../../hooks/reactQuerys/LegalRequirementQueries';

type IProps = {
  legalRequirements: ILegalRequirement[];
  legalRequirementTemplates: ILegalRequirementTemplate[];
  initialAutomation?: IAutomation;
  onClose: () => void;
  onCreateOrUpdateAutomation: (automation: IAutomation) => void;
};

const LegalRequirementAutomationForm = ({
  automation,
  setAutomation,
  legalRequirements,
  legalRequirementTemplates,
  legalRequirementStates,
}: {
  automation: ILegalRequirementAutomation;
  setAutomation: Dispatch<SetStateAction<IAutomation>>;
  legalRequirements: ILegalRequirement[];
  legalRequirementTemplates: ILegalRequirementTemplate[];
  legalRequirementStates: ILegalRequirementState[];
}) => {
  return (
    <Box>
      <Box>
        <Typography variant='body1'>
          Wenn diese Maßnahme abgeschlossen ist, setze die Anforderung...
        </Typography>
        <FormControl variant='outlined' fullWidth sx={{ mt: 2 }}>
          <InputLabel id='legal-requirement-label'>
            Anforderung wählen
          </InputLabel>
          {legalRequirements.length > 0 ? (
            <Select
              fullWidth
              labelId='legal-requirement-label'
              label='Anforderung wählen'
              value={automation.idLegalRequirement}
              onChange={e => {
                setAutomation(prev => ({
                  ...prev,
                  idLegalRequirement: Number(e.target.value),
                }));
              }}
            >
              {legalRequirements.map(requirement => (
                <MenuItem
                  key={requirement.idLegalRequirement}
                  value={requirement.idLegalRequirement}
                >
                  {requirement.LegalRequirement}
                </MenuItem>
              ))}
            </Select>
          ) : (
            legalRequirementTemplates.length > 0 && (
              <Select
                fullWidth
                labelId='legal-requirement-label'
                label='Anforderung wählen'
                value={automation.idLegalRequirementTemplate}
                onChange={e => {
                  setAutomation(prev => ({
                    ...prev,
                    idLegalRequirementTemplate: Number(e.target.value),
                  }));
                }}
              >
                {legalRequirementTemplates.map(requirement => (
                  <MenuItem
                    key={requirement.idLegalRequirementTemplate}
                    value={requirement.idLegalRequirementTemplate}
                  >
                    {requirement.LegalRequirementTemplate}
                  </MenuItem>
                ))}
              </Select>
            )
          )}
        </FormControl>
      </Box>

      <Box mt={4}>
        <Typography variant='body1'>Auf den Status...</Typography>
        <FormControl variant='outlined' fullWidth sx={{ mt: 2 }}>
          <InputLabel id='legal-requirement-state-label'>
            Status wählen
          </InputLabel>
          <Select
            fullWidth
            labelId='legal-requirement-state-label'
            label='Status wählen'
            value={automation.idLegalRequirementState}
            onChange={e => {
              setAutomation(prev => ({
                ...prev,
                idLegalRequirementState: Number(e.target.value),
              }));
            }}
          >
            {legalRequirementStates.map(state => (
              <MenuItem
                key={state.idLegalRequirementState}
                value={state.idLegalRequirementState}
              >
                {state.LegalRequirementState}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

const LegalTransactionStateAutomationForm = ({
  automation,
  setAutomation,
  legalTransactionStates,
}: {
  automation: ILegalTransactionStateAutomation;
  setAutomation: Dispatch<SetStateAction<IAutomation>>;
  legalTransactionStates: ILegalTransactionState[];
}) => {
  return (
    <Box>
      <Typography variant='body1'>
        Wenn diese Maßnahme abgeschlossen ist, setze den Status des Vorgangs
        auf...
      </Typography>
      <FormControl variant='outlined' fullWidth sx={{ mt: 2 }}>
        <InputLabel id='legal-requirement-label'>Status wählen</InputLabel>
        <Select
          fullWidth
          labelId='legal-requirement-label'
          label='Anforderung wählen'
          value={automation.idLegalTransactionState}
          onChange={e => {
            setAutomation(prev => ({
              ...prev,
              idLegalTransactionState: Number(e.target.value),
            }));
          }}
        >
          {legalTransactionStates.map(state => (
            <MenuItem
              key={state.idLegalTransactionState}
              value={state.idLegalTransactionState}
            >
              {state.LegalTransactionState}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

const LegalTransactionProgressAutomationForm = ({
  automation,
  setAutomation,
  legalTransactionProgresses,
}: {
  automation: ILegalTransactionProgressAutomation;
  setAutomation: Dispatch<SetStateAction<IAutomation>>;
  legalTransactionProgresses: ILegalTransactionProgress[];
}) => {
  return (
    <Box>
      <Typography variant='body1'>
        Wenn die Maßnahme abgeschlossen ist, setze den Status des Vorgangs
        auf...
      </Typography>
      <FormControl variant='outlined' fullWidth sx={{ mt: 2 }}>
        <InputLabel id='legal-requirement-label'>Status wählen</InputLabel>
        <Select
          fullWidth
          labelId='legal-requirement-label'
          label='Anforderung wählen'
          value={automation.idLegalTransactionProgress}
          onChange={e => {
            setAutomation(prev => ({
              ...prev,
              idLegalTransactionProgress: Number(e.target.value),
            }));
          }}
        >
          {legalTransactionProgresses.map(progress => (
            <MenuItem
              key={progress.idLegalTransactionProgress}
              value={progress.idLegalTransactionProgress}
            >
              {progress.LegalTransactionProgress}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export const CreateOrUpdateAutomationDialog = ({
  legalRequirements,
  legalRequirementTemplates,
  initialAutomation,
  onClose,
  onCreateOrUpdateAutomation,
}: IProps) => {
  const { legalRequirementStates, isLoadingLegalRequirementStates } =
    useGetLegalRequirementStates();

  const { legalTransactionProgresses, isLoadingLegalTransactionProgresses } =
    useGetLegalTransactionProgresses();

  const { legalTransactionStates, isLoadingLegalTransactionStates } =
    useGetLegalTransactionStates();

  const { showAlert } = useAlert();
  const [automation, setAutomation] = useState<IAutomation>(
    initialAutomation || getInitialRequirementAutomation()
  );

  const handleCreateOrUpdateAutomation = (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    try {
      automationSchema.parse(automation);

      onCreateOrUpdateAutomation(automation);
    } catch (error) {
      showAlert({
        text: 'Bitte füllen Sie alle Felder korrekt aus.',
        severity: 'error',
      });
    }
  };

  const handleChangeAutomationType = (
    e: SelectChangeEvent<IAutomationExecutionType>
  ) => {
    const type = e.target.value as IAutomationExecutionType;

    if (type === IAutomationExecutionType.legal_requirement) {
      setAutomation(prev => ({
        ...getInitialRequirementAutomation(),
        idAutomation: prev.idAutomation,
      }));
    } else if (type === IAutomationExecutionType.legal_transaction_state) {
      setAutomation(prev => ({
        ...getInitialStateAutomation(),
        idAutomation: prev.idAutomation,
      }));
    } else if (type === IAutomationExecutionType.legal_transaction_progress) {
      setAutomation(prev => ({
        ...getInitialProgressAutomation(),
        idAutomation: prev.idAutomation,
      }));
    }
  };

  if (
    isLoadingLegalRequirementStates ||
    isLoadingLegalTransactionProgresses ||
    isLoadingLegalTransactionStates
  ) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth='md'>
        <LoadingSpinner height='40vh' />
      </Dialog>
    );
  }

  if (
    !legalRequirementStates ||
    !legalTransactionProgresses ||
    !legalTransactionStates
  ) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth='md'>
        <ErrorScreen height='40vh' />
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth='sm'>
      <form onSubmit={handleCreateOrUpdateAutomation}>
        <DialogTitle>
          {initialAutomation
            ? 'Automatisierung bearbeiten'
            : 'Automatisierung hinzufügen'}
        </DialogTitle>
        <DialogContent sx={{ minHeight: '40vh' }}>
          <FormControl variant='outlined' fullWidth sx={{ mt: 2 }}>
            <InputLabel id='automation-type-label'>
              Automatisierungstyp wählen
            </InputLabel>
            <Select
              fullWidth
              labelId='automation-type-label'
              label='Automatisierungstyp wählen'
              value={automation.idAutomationExecutionType}
              onChange={handleChangeAutomationType}
            >
              {automationExecutionTypeOptions().map(option => (
                <MenuItem key={option} value={option}>
                  {automationTypeOptionsMap[option]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box mt={4}>
            {isRequirementAutomation(automation) ? (
              <LegalRequirementAutomationForm
                automation={automation}
                setAutomation={setAutomation}
                legalRequirements={legalRequirements}
                legalRequirementTemplates={legalRequirementTemplates}
                legalRequirementStates={legalRequirementStates}
              />
            ) : isStateAutomation(automation) ? (
              <LegalTransactionStateAutomationForm
                automation={automation}
                setAutomation={setAutomation}
                legalTransactionStates={legalTransactionStates}
              />
            ) : isProgressAutomation(automation) ? (
              <LegalTransactionProgressAutomationForm
                automation={automation}
                setAutomation={setAutomation}
                legalTransactionProgresses={legalTransactionProgresses}
              />
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant='contained' color='primary' type='submit'>
            {initialAutomation ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
